import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULTS = {
  brandName: 'Rewarding',
  supportEmail: '',
  socialLinks: { instagram: '', facebook: '', twitter: '', linkedin: '' },
  metaDescription: '',
  contactPhone: '',
  contactAddress: '',
};

function deepMerge(defaults, overrides) {
  const result = {};
  for (const key of Object.keys(defaults)) {
    if (typeof defaults[key] === 'object' && defaults[key] !== null && !Array.isArray(defaults[key])) {
      result[key] = deepMerge(defaults[key], overrides?.[key] || {});
    } else {
      result[key] = overrides?.[key] !== undefined && overrides?.[key] !== null ? overrides[key] : defaults[key];
    }
  }
  return result;
}

const Field = ({ label, value, onChange, ...props }) => (
  <TextField
    label={label} size="small" fullWidth value={value || ''} onChange={(e) => onChange(e.target.value)}
    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    {...props}
  />
);

export default function AdminSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/site-settings');
        const data = await res.json();
        setSettings(deepMerge(DEFAULTS, data));
      } catch { /* keep defaults */ }
    })();
  }, []);

  const update = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateSocial = (field, value) => {
    setSettings(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/site-settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Settings saved!', severity: 'success' });
      } else {
        throw new Error();
      }
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULTS);
    setSnack({ open: true, message: 'Reset to defaults (save to apply)', severity: 'info' });
  };

  const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mb: 2, mt: 1 }}>
      {children}
    </Typography>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Site Settings</Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>Global settings for your Rewarding platform</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RestoreIcon />} onClick={handleReset} size="small" sx={{ textTransform: 'none' }}>
            Reset
          </Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving} size="small"
            sx={{ textTransform: 'none', bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e8e8e8', mb: 3 }}>
        <SectionTitle>Brand</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2 }}>
          <Field label="Brand Name" value={settings.brandName} onChange={(v) => update('brandName', v)} />
          <Field label="Meta Description" value={settings.metaDescription} onChange={(v) => update('metaDescription', v)}
            helperText="SEO description shown in search results" />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e8e8e8', mb: 3 }}>
        <SectionTitle>Contact Information</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2 }}>
          <Field label="Support Email" value={settings.supportEmail} onChange={(v) => update('supportEmail', v)} type="email" />
          <Field label="Contact Phone" value={settings.contactPhone} onChange={(v) => update('contactPhone', v)} />
          <Field label="Contact Address" value={settings.contactAddress} onChange={(v) => update('contactAddress', v)}
            sx={{ gridColumn: { md: '1 / -1' }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e8e8e8' }}>
        <SectionTitle>Social Links</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr' }, gap: 2 }}>
          <Field label="Instagram URL" value={settings.socialLinks.instagram} onChange={(v) => updateSocial('instagram', v)} placeholder="https://instagram.com/..." />
          <Field label="Facebook URL" value={settings.socialLinks.facebook} onChange={(v) => updateSocial('facebook', v)} placeholder="https://facebook.com/..." />
          <Field label="Twitter / X URL" value={settings.socialLinks.twitter} onChange={(v) => updateSocial('twitter', v)} placeholder="https://x.com/..." />
          <Field label="LinkedIn URL" value={settings.socialLinks.linkedin} onChange={(v) => updateSocial('linkedin', v)} placeholder="https://linkedin.com/..." />
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
